/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * SectionCard — single row in the builder canvas representing one section.
 * Provides the drag handle and Edit / Delete actions.
 */

import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTheme } from '../../theme/ThemeContext';
import { useNavigate } from 'react-router-dom';

const SECTION_LABELS = {
  hero:               'Hero',
  text_block:         'Text Block',
  data_cards:         'Data Cards',
  data_table:         'Data Table',
  data_list:          'Data List',
  data_timeline:      'Timeline',
  chart_bar:          'Bar Chart',
  chart_line:         'Line Chart',
  stat_widgets:       'Stat Widgets',
  image_banner:       'Image Banner',
  divider:            'Divider',
  form_builder:       'Form Builder',
  html_css_workspace: 'HTML / CSS / JS',
  public_page:        'Public Page',
};

const SECTION_ICONS = {
  hero:               'bi-image',
  text_block:         'bi-justify-left',
  data_cards:         'bi-grid',
  data_table:         'bi-table',
  data_list:          'bi-list-ul',
  data_timeline:      'bi-clock-history',
  chart_bar:          'bi-bar-chart',
  chart_line:         'bi-graph-up',
  stat_widgets:       'bi-bar-chart-line',
  image_banner:       'bi-card-image',
  divider:            'bi-hr',
  form_builder:       'bi-ui-checks',
  html_css_workspace: 'bi-code-slash',
  public_page:        'bi-window-stack',
};

export default function SectionCard({ section, isSelected, onEdit, onDelete, appId, pageId }) {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const formId = section.section_type === 'form_builder' ? section.section_config?.form_id : null;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section._clientId,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const label = SECTION_LABELS[section.section_type] || section.section_type;
  const icon = SECTION_ICONS[section.section_type] || 'bi-layout-text-window';

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        border: isSelected ? `2px solid ${theme.primary}` : '1px solid #e2e8f0',
        borderRadius: 8,
        background: isSelected ? theme.primary + '08' : '#fff',
        marginBottom: 8,
        transition: 'border-color 0.15s, background 0.15s',
        boxShadow: isDragging ? '0 8px 24px rgba(0,0,0,0.12)' : '0 1px 3px rgba(0,0,0,0.04)',
      }}
    >
      <div className="d-flex align-items-center px-3 py-2 gap-3">
        {/* Drag handle */}
        <span
          {...attributes}
          {...listeners}
          style={{ cursor: isDragging ? 'grabbing' : 'grab', color: '#94a3b8', fontSize: 16, flexShrink: 0 }}
          title="Drag to reorder"
        >
          <i className="bi bi-grip-vertical" />
        </span>

        {/* Section icon + label */}
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 6,
            background: theme.primary + '15',
            color: theme.primary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <i className={`bi ${icon}`} style={{ fontSize: 14 }} />
        </div>

        <div className="flex-grow-1">
          <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>{label}</div>
          {!section.is_visible && (
            <span className="badge bg-warning text-dark ms-1" style={{ fontSize: 10 }}>Hidden</span>
          )}
        </div>

        {/* Actions */}
        <div className="d-flex gap-1">
          {formId && appId && pageId && (
            <button
              className="btn btn-sm"
              style={{ background: '#eff6ff', color: '#2563eb', border: 'none', padding: '4px 10px', fontSize: 12, borderRadius: 6 }}
              onClick={() => navigate(`/dashboard/applications/${appId}/page-builder/${pageId}/forms/${formId}/submissions`)}
              title="View form submissions"
            >
              <i className="bi bi-inbox me-1" />
              Submissions
            </button>
          )}
          <button
            className="btn btn-sm"
            style={{
              background: isSelected ? theme.primary : '#f1f5f9',
              color: isSelected ? '#fff' : '#374151',
              border: 'none',
              padding: '4px 10px',
              fontSize: 12,
              borderRadius: 6,
            }}
            onClick={onEdit}
            title="Edit section properties"
          >
            <i className="bi bi-pencil me-1" />
            Edit
          </button>
          <button
            className="btn btn-sm"
            style={{ background: '#fee2e2', color: '#dc2626', border: 'none', padding: '4px 8px', borderRadius: 6 }}
            onClick={onDelete}
            title="Delete section"
          >
            <i className="bi bi-trash" />
          </button>
        </div>
      </div>
    </div>
  );
}
